// THE FIRM'S OWN ADDRESS, IN ONE LANGUAGE — one definition, both ends.
//
// 207 §12 [RULED — Laurent: "Firm country and address should be i18n (eg in
// FR; avenue Louise, Belgique, in NL Louizalaan, België)"], and 207B §4
// [RULED 2026-09-24, ruling 1 — Laurent: "why don't just copy the system for eg
// general conditions (3 adress boxes)?"]: the firm's address is ONE WHOLE
// ADDRESS PER LANGUAGE — French, Dutch, English — each kept apart as a street
// and number, a postal code and a city (an e-invoice wants them apart), in
// `FirmSettings.firm.addressByLang`:
//
//     { fr: { street, postalCode, city }, nl: { … }, en: { … } }
//
// The DOCUMENT's language reads its block in a document
// (server/src/lib/docGen.js), the READER's on a screen (the pro forma window's
// firm block, the default meeting location); an EMPTY block — street, postal
// code and city all blank — reads the FIRST FILLED one in the order Settings
// shows them (fr, nl, en). A firm with no block filled reads the single
// `street` / `postalCode` / `city` it held before (prod until the address
// one-shot has run — server/scripts/seed_migration_207_firm_address.js — and
// what a redeploy of `main` still prints). 207's default-plus-variants shape
// (`streetByLang` / `cityByLang`) is gone; it never reached prod.
//
// The country is ONE, a CODE of the `country` vocabulary, picked from the list
// the parties use; this file hands it back as stored, and each caller prints it
// as a word in its own language (the documents through lib/docWords.js
// `firmCountryWord`, a screen through the vocabulary it has loaded).
//
// Pure, and read by the server and the browser alike — the way
// `src/lib/periodLabel.mjs` is read by server/src/lib/docGen.js.

/** 207B §4 — the languages the firm's address is kept in, in the order Settings shows them — the order an empty block borrows from. */
export const FIRM_ADDRESS_LANGS = ["fr", "nl", "en"];

/**
 * 207 §13 [ADVISOR — Laurent overrules at the glance] — the firm's country as
 * the CODE a new client's or party's form starts with: a real value, saved with
 * the record, changeable. The stored code; a text a tenant has not yet
 * converted, through `resolve` (the one matcher, handed in — the browser's
 * `resolveTaxonomyEntry`); "" when it names no entry, or names `other`: a
 * country nobody can name is not prefilled — nothing is guessed.
 * @param {unknown} firm  `FirmSettings.firm`
 * @param {(value: string) => unknown} resolve  the value → its vocabulary entry, or null
 * @returns {string}
 */
export function firmCountryCode(firm, resolve) {
  const stored = firmAddressIn(firm, null).country;
  if (!stored) return "";
  const e = resolve(stored);
  // The matched entry's code, NAMED for its kind (docWords.js `countryWord`'s
  // reason): the taxonomy lint attributes a comparison by the compared name,
  // and `country` is this kind's column — `other` is its protected code.
  const country = e && typeof e === "object" && typeof (/** @type {{ code?: unknown }} */ (e)).code === "string" ? String((/** @type {{ code: string }} */ (e)).code) : "";
  return country && country !== "other" ? country : "";
}

/** A string field of the firm, or "". */
function text(v) {
  return typeof v === "string" ? v : "";
}

/**
 * 207B §4 — the block stored for `lang`, as typed (every field a string), or
 * null when there is none or all three of its fields are blank.
 * @param {Record<string, unknown>} f
 * @param {unknown} lang
 * @returns {{ street: string, postalCode: string, city: string } | null}
 */
function blockOf(f, lang) {
  const map = f.addressByLang;
  if (!map || typeof map !== "object" || Array.isArray(map) || typeof lang !== "string") return null;
  const b = /** @type {Record<string, unknown>} */ (map)[lang];
  if (!b || typeof b !== "object" || Array.isArray(b)) return null;
  const r = /** @type {Record<string, unknown>} */ (b);
  const out = { street: text(r.street), postalCode: text(r.postalCode), city: text(r.city) };
  return out.street.trim() || out.postalCode.trim() || out.city.trim() ? out : null;
}

/**
 * The firm's address as it reads in `lang`: that language's block; an empty
 * block, the first filled one in the order shown (fr, nl, en); no block filled,
 * the single fields the firm held before 207B. The country as stored.
 * @param {unknown} firm  `FirmSettings.firm` (untyped JSON), or nothing
 * @param {unknown} lang  a language code (fr / nl / en); anything else reads the first filled block
 * @returns {{ street: string, postalCode: string, city: string, country: string }}
 */
export function firmAddressIn(firm, lang) {
  const f = firm && typeof firm === "object" && !Array.isArray(firm) ? /** @type {Record<string, unknown>} */ (firm) : {};
  const block =
    blockOf(f, lang) ||
    FIRM_ADDRESS_LANGS.map((l) => blockOf(f, l)).find(Boolean) ||
    { street: text(f.street), postalCode: text(f.postalCode), city: text(f.city) };
  return { ...block, country: text(f.country) };
}
